import { Component, ChangeDetectionStrategy, input, computed } from "@angular/core";
import { DatePipe } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { TagModule } from "primeng/tag";
import { CoreCurrencyPipe } from "src/app/shared/pipe/core-currency.pipe";

import { Store } from "@ngrx/store";
import { inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";

import { QuoteCreditCheck } from "src/api-service";
import {
    CreditCheckOutcome,
    isDeniedByLimit,
    isDeniedByScore,
} from "src/app/store/application/data/credit-check/quote/decide-credit-check";
import { selectCreditCheckOutcome } from "src/app/store/application/data/credit-check/quote/quote-credit-check.selectors";

/**
 * Read-only rendering of a stored credit-check result.
 *
 * Shared by the step dialog and the Billing tab so the two can't drift. The verdict shown is
 * re-derived against the total passed in, not read from the record — a check approved at one order
 * value is not approved at a larger one, and the user needs to see that.
 */
@Component({
    selector: "app-quote-credit-check-status",
    templateUrl: "./quote-credit-check-status.component.html",
    styleUrl: "./quote-credit-check-status.component.scss",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DatePipe, TagModule, TranslateModule, CoreCurrencyPipe],
})
export class QuoteCreditCheckStatusComponent {

    public readonly check = input<QuoteCreditCheck | null | undefined>(null);
    public readonly currentTotal = input<number>(0);

    /**
     * Off where the host already spells the denial out — the step dialog folds the reasons into its
     * error message rather than showing them twice.
     */
    public readonly showDenialReasons = input<boolean>(true);

    private readonly store = inject(Store);

    /**
     * Read from the store rather than re-derived per call site. Deriving it here meant every
     * caller had to plumb the customer identifiers, and the two that didn't rendered a different
     * verdict for the same record than the Billing tab did.
     */
    private readonly outcome = toSignal(
        this.store.select(selectCreditCheckOutcome),
        { initialValue: CreditCheckOutcome.NoCheck },
    );

    public readonly isApproved = computed(() => this.outcome() === CreditCheckOutcome.Approved);
    public readonly isStale = computed(() => this.outcome() === CreditCheckOutcome.Stale);
    public readonly isOverridden = computed(() => !!this.check()?.overriddenBy);

    /** Distinguishes "score too low" from "quote outgrew the limit" — different remedies. */
    public readonly deniedByScore = computed(() => isDeniedByScore(this.check()));

    public readonly deniedByLimit = computed(() => isDeniedByLimit(this.check(), this.currentTotal()));

    /** How much room is left before the quote exceeds the credit limit. */
    public readonly headroom = computed(() => {
        const check = this.check();
        return check ? check.creditLimit - this.currentTotal() : 0;
    });

    /** True when the quote has moved since the check was taken, so the value shown is worth noting. */
    public readonly valueChangedSinceCheck = computed(() => {
        const check = this.check();
        return !!check && check.checkedValue !== this.currentTotal();
    });
}
